/**
 * Video Cover Block - Editor Component
 *
 * @package BrandyBlocks
 */

import { __ } from "@wordpress/i18n";
import {
  useBlockProps,
  InspectorControls,
  BlockControls,
  InnerBlocks,
  MediaUpload,
  MediaUploadCheck,
  __experimentalBlockAlignmentMatrixControl as BlockAlignmentMatrixControl,
  __experimentalColorGradientSettingsDropdown as ColorGradientSettingsDropdown,
  __experimentalUseMultipleOriginColorsAndGradients as useMultipleOriginColorsAndGradients,
  useSetting,
} from "@wordpress/block-editor";
import { useMemo } from "@wordpress/element";
import {
  PanelBody,
  Button,
  ToggleControl,
  TextControl,
  SelectControl,
  RangeControl,
  Placeholder,
  ToolbarButton,
  __experimentalUnitControl as UnitControl,
  __experimentalToolsPanel as ToolsPanel,
  __experimentalToolsPanelItem as ToolsPanelItem,
} from "@wordpress/components";
import { fullscreen } from "@wordpress/icons";

/**
 * Parse video URL to detect type and ID.
 *
 * @param {string} url Video URL.
 * @return {Object} Video type and ID.
 */
function parseVideoUrl(url) {
  if (!url) return { type: "upload", id: null };

  // youtu.be puts the id straight after the host, with no path prefix.
  const ytShort = url.match(/youtu\.be\/([\w-]{11})/);
  if (ytShort) return { type: "youtube", id: ytShort[1] };

  /*
   * Long form. The prefix is required — matching a bare 11 characters after
   * the host would claim channel and playlist URLs too. `v=` is matched
   * anywhere in the query because a share link often carries other parameters
   * ahead of it (`watch?app=desktop&v=…`), which the old pattern missed and
   * silently treated as a self-hosted file.
   */
  const ytLong = url.match(
    /(?:youtube\.com|youtube-nocookie\.com)\/(?:watch\?(?:\S*&)?v=|embed\/|shorts\/|live\/|v\/)([\w-]{11})/
  );
  if (ytLong) return { type: "youtube", id: ytLong[1] };

  // Also covers /channels/<name>/<id> and player.vimeo.com/video/<id>.
  const vimeoMatch = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
  if (vimeoMatch) return { type: "vimeo", id: vimeoMatch[1] };

  // Self-hosted
  return { type: "upload", id: null };
}

/**
 * Get overlay color style.
 *
 * @param {Object} attributes Block attributes.
 * @return {string} Overlay color value.
 */
function getOverlayColor(attributes) {
  const { overlayColor, customOverlayColor } = attributes;
  return customOverlayColor || overlayColor || "";
}

/**
 * Edit component for Video Cover block.
 *
 * @param {Object}   props               Block props.
 * @param {Object}   props.attributes    Block attributes.
 * @param {Function} props.setAttributes Function to update attributes.
 * @param {string}   props.clientId      Block client ID.
 * @return {JSX.Element} Editor component.
 */
export default function Edit({ attributes, setAttributes, clientId }) {
  const {
    videoType,
    videoUrl,
    videoId,
    externalVideoId,
    poster,
    autoplay,
    loop,
    muted,
    showPlayButton,
    showMuteButton,
    contentPosition,
    isFullHeight,
    minHeight,
    dimRatio,
  } = attributes;

  const overlayColorValue = getOverlayColor(attributes);
  const colorGradientSettings = useMultipleOriginColorsAndGradients();

  const parsedContentPosition = useMemo(() => {
    let result = contentPosition;
    if (result === "center center") {
      result = "center";
    }
    return result.replace(" ", "-");
  }, [contentPosition]);

  const blockProps = useBlockProps({
    className: `brandy-video-cover content-position-${parsedContentPosition.replace(
      " ",
      "-"
    )}${isFullHeight ? " is-full-height" : ""}`,
    style: {
      minHeight: isFullHeight ? "100vh" : minHeight,
    },
  });

  const hasVideo = videoUrl || externalVideoId;

  /**
   * Handle video URL change for external videos.
   *
   * @param {string}  url           External video URL.
   * @param {boolean} allowFallback Whether an unrecognised URL may be taken as
   *                                a direct file link. True only at the
   *                                placeholder, where pasting an .mp4 is a
   *                                legitimate way to use the block.
   */
  const handleExternalUrlChange = (url, allowFallback = false) => {
    const parsed = parseVideoUrl(url);

    setAttributes({
      videoUrl: url,
      /*
       * Reporting an unrecognised URL as "upload" switched the panel over to
       * the media library, which took this field away mid-edit — a single
       * typo in a YouTube link was enough to lose the control being typed in.
       * Holding the type still lets the author finish or correct the URL.
       */
      videoType: parsed.id || allowFallback ? parsed.type : videoType,
      externalVideoId: parsed.id || "",
    });
  };

  /**
   * Handle self-hosted video selection.
   *
   * @param {Object} media Media object from library.
   */
  const handleVideoSelect = (media) => {
    setAttributes({
      videoUrl: media.url,
      videoId: media.id,
      videoType: "upload",
      externalVideoId: "",
    });
  };

  /**
   * Handle poster image selection.
   *
   * @param {Object} media Media object from library.
   */
  const handlePosterSelect = (media) => {
    setAttributes({
      poster: media.url,
      posterId: media.id,
    });
  };

  /**
   * Toggle full height mode.
   */
  const toggleFullHeight = () => {
    setAttributes({ isFullHeight: !isFullHeight });
  };

  /**
   * Render video preview based on type.
   */
  const renderVideoPreview = () => {
    if (videoType === "youtube" && externalVideoId) {
      /*
       * YouTube verifies the embedding site before it will build a player, and
       * refuses with "Video player configuration error" (error 153) when it
       * cannot. Naming the origin outright is what the front end already does,
       * and is what makes the check pass on hosts YouTube cannot resolve from
       * the referrer alone — a local install, or any admin screen whose
       * referrer policy is tightened past the WordPress default.
       *
       * `enablejsapi` is what makes `origin` authoritative; YouTube ignores the
       * parameter without it. Nothing here talks to the JS API.
       */
      const previewParams = new URLSearchParams({
        autoplay: "0",
        mute: "1",
        controls: "0",
        loop: loop ? "1" : "0",
        enablejsapi: "1",
        origin: window.location.origin,
      });

      return (
        /*
         * The editor preview is a still, non-interactive frame — it never
         * needs the JS API — so it can use the no-cookie host and skip
         * setting tracking cookies in wp-admin. That matters more than usual
         * here: the block's inserter example is a YouTube video, so this frame
         * renders for anyone who merely opens the inserter.
         */
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${externalVideoId}?${previewParams}`}
          title={__("Background video preview", "brandy-blocks")}
          loading="lazy"
          frameBorder="0"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="brandy-video-cover-iframe"
        />
      );
    }

    if (videoType === "vimeo" && externalVideoId) {
      return (
        <iframe
          src={`https://player.vimeo.com/video/${externalVideoId}?autoplay=0&muted=1&loop=${
            loop ? 1 : 0
          }&background=1`}
          title={__("Background video preview", "brandy-blocks")}
          loading="lazy"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="brandy-video-cover-iframe"
        />
      );
    }

    if (videoType === "upload" && videoUrl) {
      return (
        <video
          src={videoUrl}
          poster={poster}
          muted
          loop={loop}
          playsInline
          className="brandy-video-cover-video"
        />
      );
    }

    return null;
  };

  return (
    <>
      {hasVideo && (
        <BlockControls group="block">
          <BlockAlignmentMatrixControl
            label={__("Change content position", "brandy-blocks")}
            value={contentPosition}
            onChange={(value) => setAttributes({ contentPosition: value })}
          />
          <ToolbarButton
            icon={fullscreen}
            label={__("Full height", "brandy-blocks")}
            isPressed={isFullHeight}
            onClick={toggleFullHeight}
          />
        </BlockControls>
      )}

      <InspectorControls>
        <PanelBody title={__("Video Settings", "brandy-blocks")} initialOpen>
          <SelectControl
            label={__("Video Source", "brandy-blocks")}
            value={videoType}
            options={[
              { label: __("Upload Video", "brandy-blocks"), value: "upload" },
              { label: __("YouTube URL", "brandy-blocks"), value: "youtube" },
              { label: __("Vimeo URL", "brandy-blocks"), value: "vimeo" },
            ]}
            onChange={(value) => setAttributes({ videoType: value })}
          />

          {videoType === "upload" ? (
            <div className="brandy-video-cover-upload-control">
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={handleVideoSelect}
                  allowedTypes={["video"]}
                  value={videoId}
                  render={({ open }) => (
                    <>
                      {videoUrl ? (
                        <div className="brandy-video-preview-thumbnail">
                          <video
                            src={videoUrl}
                            muted
                            style={{ width: "100%" }}
                          />
                          <div className="brandy-video-actions">
                            <Button variant="secondary" onClick={open}>
                              {__("Replace", "brandy-blocks")}
                            </Button>
                            <Button
                              variant="link"
                              isDestructive
                              onClick={() =>
                                setAttributes({ videoUrl: "", videoId: 0 })
                              }
                            >
                              {__("Remove", "brandy-blocks")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button variant="secondary" onClick={open}>
                          {__("Select Video", "brandy-blocks")}
                        </Button>
                      )}
                    </>
                  )}
                />
              </MediaUploadCheck>
            </div>
          ) : (
            <TextControl
              label={__("Video URL", "brandy-blocks")}
              value={videoUrl}
              onChange={handleExternalUrlChange}
              placeholder={
                videoType === "youtube"
                  ? "https://youtube.com/watch?v=..."
                  : "https://vimeo.com/..."
              }
              help={
                externalVideoId
                  ? __("Video ID: ", "brandy-blocks") + externalVideoId
                  : videoUrl
                  ? __(
                      "No video ID found in that URL — check it and try again.",
                      "brandy-blocks"
                    )
                  : ""
              }
            />
          )}

          <div style={{ marginTop: "16px" }}>
            <p style={{ marginBottom: "8px" }}>
              {__("Poster Image (Thumbnail)", "brandy-blocks")}
            </p>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={handlePosterSelect}
                allowedTypes={["image"]}
                value={attributes.posterId}
                render={({ open }) => (
                  <>
                    {poster ? (
                      <div className="brandy-poster-preview">
                        <img src={poster} alt="" style={{ width: "100%" }} />
                        <div className="brandy-video-actions">
                          <Button variant="secondary" onClick={open}>
                            {__("Replace", "brandy-blocks")}
                          </Button>
                          <Button
                            variant="link"
                            isDestructive
                            onClick={() =>
                              setAttributes({ poster: "", posterId: 0 })
                            }
                          >
                            {__("Remove", "brandy-blocks")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="secondary" onClick={open}>
                        {__("Select Poster", "brandy-blocks")}
                      </Button>
                    )}
                  </>
                )}
              />
            </MediaUploadCheck>
          </div>
        </PanelBody>

        <PanelBody title={__("Playback Settings", "brandy-blocks")}>
          <ToggleControl
            label={__("Autoplay", "brandy-blocks")}
            checked={autoplay}
            onChange={(value) => setAttributes({ autoplay: value })}
            help={__(
              "Video will autoplay when muted (browser requirement)",
              "brandy-blocks"
            )}
          />
          <ToggleControl
            label={__("Loop", "brandy-blocks")}
            checked={loop}
            onChange={(value) => setAttributes({ loop: value })}
          />
          <ToggleControl
            label={__("Start Muted", "brandy-blocks")}
            checked={muted}
            onChange={(value) => setAttributes({ muted: value })}
          />
        </PanelBody>

        <PanelBody title={__("Controls", "brandy-blocks")}>
          <ToggleControl
            label={__("Show Play Button", "brandy-blocks")}
            checked={showPlayButton}
            onChange={(value) => setAttributes({ showPlayButton: value })}
          />
          <ToggleControl
            label={__("Show Mute Button", "brandy-blocks")}
            checked={showMuteButton}
            onChange={(value) => setAttributes({ showMuteButton: value })}
          />
        </PanelBody>

        <PanelBody
          title={__("Dimensions", "brandy-blocks")}
          initialOpen={false}
        >
          <ToggleControl
            label={__("Full height", "brandy-blocks")}
            checked={isFullHeight}
            onChange={(value) => setAttributes({ isFullHeight: value })}
            help={__(
              "Toggle to set the block to full viewport height",
              "brandy-blocks"
            )}
          />
          {!isFullHeight && (
            <UnitControl
              label={__("Minimum Height", "brandy-blocks")}
              value={minHeight}
              onChange={(value) => setAttributes({ minHeight: value })}
              units={[
                { value: "px", label: "px" },
                { value: "vh", label: "vh" },
                { value: "em", label: "em" },
              ]}
            />
          )}
        </PanelBody>
      </InspectorControls>

      {/* Overlay controls in Color/Styles panel */}
      <InspectorControls group="color">
        <ColorGradientSettingsDropdown
          __experimentalIsRenderedInSidebar
          settings={[
            {
              label: __("Overlay", "brandy-blocks"),
              colorValue: overlayColorValue,
              onColorChange: (value) =>
                setAttributes({ customOverlayColor: value }),
            },
          ]}
          panelId={clientId}
          {...colorGradientSettings}
        />
        <div style={{ gridColumn: "span 2" }}>
          <RangeControl
            label={__("Overlay opacity", "brandy-blocks")}
            value={dimRatio}
            onChange={(value) => setAttributes({ dimRatio: value })}
            min={0}
            max={100}
            step={10}
            __nextHasNoMarginBottom
          />
        </div>
      </InspectorControls>

      <div {...blockProps}>
        {!hasVideo ? (
          <Placeholder
            icon="video-alt3"
            label={__("Video Cover", "brandy-blocks")}
            instructions={__(
              "Upload a video or paste a YouTube/Vimeo URL",
              "brandy-blocks"
            )}
          >
            <div className="brandy-video-cover-placeholder-actions">
              <MediaUploadCheck>
                <MediaUpload
                  onSelect={handleVideoSelect}
                  allowedTypes={["video"]}
                  render={({ open }) => (
                    <Button variant="primary" onClick={open}>
                      {__("Upload Video", "brandy-blocks")}
                    </Button>
                  )}
                />
              </MediaUploadCheck>
              <span className="brandy-video-cover-placeholder-or">
                {__("or", "brandy-blocks")}
              </span>
              <TextControl
                placeholder={__("Paste YouTube or Vimeo URL", "brandy-blocks")}
                value={videoUrl}
                onChange={(url) => handleExternalUrlChange(url, true)}
                className="brandy-video-cover-url-input"
              />
            </div>
          </Placeholder>
        ) : (
          <>
            <div className="brandy-video-cover-media">
              {renderVideoPreview()}
              {overlayColorValue && dimRatio > 0 && (
                <div
                  className="brandy-video-cover-overlay"
                  style={{
                    backgroundColor: overlayColorValue,
                    opacity: dimRatio / 100,
                  }}
                />
              )}
            </div>

            {showPlayButton && (
              <button
                type="button"
                className="brandy-video-cover-play-btn"
                aria-label={__("Play/Pause", "brandy-blocks")}
              >
                <svg
                  width="19"
                  height="20"
                  viewBox="0 0 19 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 10V5.82299C0 0.636928 3.71397 -1.48678 8.25855 1.10626L11.9251 3.19476L15.5916 5.28326C20.1361 7.8763 20.1361 12.1237 15.5916 14.7167L11.9251 16.8052L8.25855 18.8937C3.71397 21.4868 0 19.3631 0 14.177V10Z"
                    fill="white"
                  />
                </svg>
              </button>
            )}

            {showMuteButton && (
              <button
                type="button"
                className="brandy-video-cover-mute-btn"
                aria-label={__("Mute/Unmute", "brandy-blocks")}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.5314 13.4199L21.0814 11.9699L22.4814 10.5699C22.7714 10.2799 22.7714 9.79993 22.4814 9.50993C22.1914 9.21993 21.7114 9.21993 21.4214 9.50993L20.0214 10.9099L18.5714 9.45993C18.2814 9.16993 17.8014 9.16993 17.5114 9.45993C17.2214 9.74993 17.2214 10.2299 17.5114 10.5199L18.9614 11.9699L17.4714 13.4599C17.1814 13.7499 17.1814 14.2299 17.4714 14.5199C17.6214 14.6699 17.8114 14.7399 18.0014 14.7399C18.1914 14.7399 18.3814 14.6699 18.5314 14.5199L20.0214 13.0299L21.4714 14.4799C21.6214 14.6299 21.8114 14.6999 22.0014 14.6999C22.1914 14.6999 22.3814 14.6299 22.5314 14.4799C22.8214 14.1899 22.8214 13.7199 22.5314 13.4199Z"
                    fill="white"
                  />
                  <path
                    d="M14.02 3.77997C12.9 3.15997 11.47 3.31997 10.01 4.22997L7.09 6.05997C6.89 6.17997 6.66 6.24997 6.43 6.24997H5.5H5C2.58 6.24997 1.25 7.57997 1.25 9.99997V14C1.25 16.42 2.58 17.75 5 17.75H5.5H6.43C6.66 17.75 6.89 17.82 7.09 17.94L10.01 19.77C10.89 20.32 11.75 20.59 12.55 20.59C13.07 20.59 13.57 20.47 14.02 20.22C15.13 19.6 15.75 18.31 15.75 16.59V7.40997C15.75 5.68997 15.13 4.39997 14.02 3.77997Z"
                    fill="white"
                  />
                </svg>
              </button>
            )}

            <div className="brandy-video-cover-content">
              <InnerBlocks
                template={[
                  [
                    "core/paragraph",
                    {
                      placeholder: __("Add content here...", "brandy-blocks"),
                    },
                  ],
                ]}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
